import { MUTE_KEY } from "./constants";

type SfxName =
  | "move"
  | "rotate"
  | "lock"
  | "hold"
  | "soft"
  | "hard"
  | "clear"
  | "tetris"
  | "tspin"
  | "over";

function readMuted(): boolean {
  if (typeof window === "undefined") return true;
  const stored = window.localStorage.getItem(MUTE_KEY);
  return stored === null ? true : stored === "1";
}

export class TetrisAudio {
  private ctx: AudioContext | null = null;
  private muted = readMuted();

  isMuted(): boolean {
    return this.muted;
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (typeof window !== "undefined") {
      window.localStorage.setItem(MUTE_KEY, muted ? "1" : "0");
    }
  }

  toggle(): boolean {
    this.setMuted(!this.muted);
    if (!this.muted) this.unlock();
    return this.muted;
  }

  unlock(): void {
    if (typeof window === "undefined") return;
    if (!this.ctx) {
      const AudioCtx =
        window.AudioContext ||
        (window as Window & { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext;
      if (!AudioCtx) return;
      this.ctx = new AudioCtx();
    }
    if (this.ctx.state === "suspended") {
      void this.ctx.resume();
    }
  }

  play(name: SfxName): void {
    if (this.muted) return;
    this.unlock();
    const ctx = this.ctx;
    if (!ctx) return;

    switch (name) {
      case "move":
        this.blip(ctx, 420, 0.045, "square", 0.04);
        break;
      case "rotate":
        this.blip(ctx, 620, 0.06, "triangle", 0.05);
        break;
      case "lock":
        this.thud(ctx, 140, 0.09);
        break;
      case "hold":
        this.sweep(ctx, 280, 520, 0.1);
        break;
      case "soft":
        this.blip(ctx, 240, 0.03, "square", 0.03);
        break;
      case "hard":
        this.thud(ctx, 90, 0.12);
        this.blip(ctx, 880, 0.05, "sine", 0.04);
        break;
      case "clear":
        this.arpeggio(ctx, [520, 680, 860], 0.07);
        break;
      case "tetris":
        this.arpeggio(ctx, [392, 523, 659, 784, 1046], 0.09);
        break;
      case "tspin":
        this.arpeggio(ctx, [466, 622, 784, 932], 0.08);
        break;
      case "over":
        this.sweep(ctx, 420, 90, 0.42);
        break;
      default:
        break;
    }
  }

  private blip(
    ctx: AudioContext,
    freq: number,
    duration: number,
    type: OscillatorType,
    gain: number
  ): void {
    const osc = ctx.createOscillator();
    const amp = ctx.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    amp.gain.setValueAtTime(gain, ctx.currentTime);
    amp.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
    osc.connect(amp);
    amp.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + duration);
  }

  private thud(ctx: AudioContext, freq: number, duration: number): void {
    const osc = ctx.createOscillator();
    const amp = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(freq, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(40, ctx.currentTime + duration);
    amp.gain.setValueAtTime(0.12, ctx.currentTime);
    amp.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
    osc.connect(amp);
    amp.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + duration);
  }

  private sweep(
    ctx: AudioContext,
    from: number,
    to: number,
    duration: number
  ): void {
    const osc = ctx.createOscillator();
    const amp = ctx.createGain();
    osc.type = "sawtooth";
    osc.frequency.setValueAtTime(from, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(
      Math.max(40, to),
      ctx.currentTime + duration
    );
    amp.gain.setValueAtTime(0.05, ctx.currentTime);
    amp.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
    osc.connect(amp);
    amp.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + duration);
  }

  private arpeggio(ctx: AudioContext, notes: number[], step: number): void {
    notes.forEach((freq, index) => {
      const osc = ctx.createOscillator();
      const amp = ctx.createGain();
      const start = ctx.currentTime + index * step;
      osc.type = "triangle";
      osc.frequency.value = freq;
      amp.gain.setValueAtTime(0.0001, start);
      amp.gain.exponentialRampToValueAtTime(0.07, start + 0.02);
      amp.gain.exponentialRampToValueAtTime(0.001, start + step + 0.04);
      osc.connect(amp);
      amp.connect(ctx.destination);
      osc.start(start);
      osc.stop(start + step + 0.05);
    });
  }
}

export const tetrisAudio = new TetrisAudio();
